'use client';
import { forwardRef, type ImgHTMLAttributes, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { cn, imageErrorHandler } from '@/app/utils';

interface ImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  placeHolderClassName?: string;
  alt: string;
  fullScreen?: boolean;
  disableDownload?: boolean;
  fullScreenWatermark?: boolean;
}

interface ImageNaturalSize {
  width: number;
  height: number;
}

const FullScreenImage = ({
  src,
  alt,
  onClose,
  disableDownload = false,
  showWatermark = false,
  naturalSize,
}: {
  src: string;
  alt: string;
  onClose: () => void;
  disableDownload?: boolean;
  showWatermark?: boolean;
  naturalSize?: ImageNaturalSize | null;
}) => {
  const image = (
    <img
      src={src}
      alt={alt}
      className={cn('size-full cursor-zoom-out object-contain', disableDownload && 'select-none')}
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
      draggable={!disableDownload}
    />
  );

  return createPortal(
    <div
      className="pointer-events-auto fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-2xl"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div className="pointer-events-auto relative size-full p-4">
        <button
          type="button"
          className="fixed top-4 right-4 flex h-9 w-9 items-center justify-center rounded-8 border border-system-white12 bg-system-white08 text-base text-system-white hover:bg-system-hover01 md:hidden"
          onClick={onClose}
        >
          <BaseLineClose className="size-4" />
        </button>
        {showWatermark && naturalSize ? (
          <MediaWithWatermark width={naturalSize.width} height={naturalSize.height} showWatermark>
            {image}
          </MediaWithWatermark>
        ) : (
          image
        )}
      </div>
    </div>,
    document.body,
  );
};

// 此处必须用 forwardRef 包裹，因为在 CustomTooltip 中使用时，会报错
export const ImageWithPlaceholder = forwardRef<HTMLImageElement, ImageProps>(
  (
    {
      className,
      style,
      placeHolderClassName,
      src,
      alt,
      onLoad,
      fullScreen = false,
      disableDownload = false,
      fullScreenWatermark = false,
      loading = 'lazy',
      decoding = 'async',
      ...props
    },
    ref,
  ) => {
    const [isLoading, setIsLoading] = useState(true);
    const [showFullScreen, setShowFullScreen] = useState(false);
    const [naturalSize, setNaturalSize] = useState<ImageNaturalSize | null>(null);
    const innerRef = useRef<HTMLImageElement>(null);

    const imageRef = ref || innerRef;

    useEffect(() => {
      setIsLoading(true);
    }, [src]);

    // 解决 nextjs 图片从缓存中加载时不会触发onload事件的问题
    // https://stackoverflow.com/questions/59787642/nextjs-images-loaded-from-cache-dont-trigger-the-onload-event
    useEffect(() => {
      if (typeof imageRef === 'object' && imageRef?.current?.complete) {
        setNaturalSize({ width: imageRef.current.naturalWidth, height: imageRef.current.naturalHeight });
        setIsLoading(false);
      }
    }, [imageRef]);

    const handleImageClick = () => {
      if (fullScreen && !isLoading) {
        setShowFullScreen(true);
      }
    };

    return (
      <>
        {isLoading && (
          <div style={style} className={cn(className, 'animate-pulse bg-system-text05', placeHolderClassName)} />
        )}
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          loading={loading}
          decoding={decoding}
          style={{
            ...style,
            ...(isLoading
              ? {
                  position: 'absolute',
                  opacity: 0,
                  pointerEvents: 'none',
                }
              : {}),
          }}
          className={cn(className, fullScreen && 'cursor-zoom-in', disableDownload && 'select-none')}
          onLoad={(e) => {
            onLoad?.(e);
            setNaturalSize({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight });
            setIsLoading(false);
          }}
          onError={imageErrorHandler}
          onClick={handleImageClick}
          onContextMenu={disableDownload ? (e) => e.preventDefault() : undefined}
          draggable={!disableDownload}
          {...props}
        />
        {showFullScreen && src && (
          <FullScreenImage
            src={src as string}
            alt={alt}
            disableDownload={disableDownload}
            showWatermark={fullScreenWatermark}
            naturalSize={naturalSize}
            onClose={() => {
              setShowFullScreen(false);
            }}
          />
        )}
      </>
    );
  },
);

ImageWithPlaceholder.displayName = 'ImageWithPlaceholder';
